import {
    DataPacket_Kind,
    LocalParticipant,
    RemoteParticipant,
    Room,
    RoomEvent,
    setLogLevel,
    VideoPresets,
    VideoQuality,
} from 'livekit-client';
import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import 'react-aspect-ratio/aspect-ratio.css';
import {
    DisplayContext,
    DisplayOptions,
    FullscreenContext,
    UserSettingsContent,
    useSettingsContext,
    useUserSettingsContext,
} from 'contexts';
import { LiveKitRoom } from 'features/Room/LiveKitRoom';
import { useRoomVirtualBackground } from 'features/VirtualBackground/utils/setupVirtualBackgrounds';
import { useApplySpeakerVolume } from 'features/VolumeSlider/hooks/useApplySpeakerVolume';
import { useWebinarForceCamOff } from 'useCase/webinar/useWebinarForceCamOff';
import { useWebinarForceMicOff } from 'useCase/webinar/useWebinarForceMicOff';
import { useWebinarForceScreenShareOff } from 'useCase/webinar/useWebinarForceScreenShareOff';
import { resetWebinarSpeakerLayoutOptOut, useWebinarAutoLayout } from 'useCase/webinar/useWebinarAutoLayout';
import { useWebinarModeNotifications } from 'useCase/webinar/useWebinarModeNotifications';
import { getPersistedWebinarLocks, persistWebinarLocksForPrejoin } from 'useCase/webinar/webinarPrejoinLocks';
import { isCameraHardLocked, isMicHardLocked } from 'useCase/webinar/webinarStatus';
import { useMutedSpeechDetection } from 'features/MutedSpeechDetection/hooks/useMutedSpeechDetection';
import { QualitySelector, SelectOption } from 'ui/components';
import { useParticipantItem } from 'features/ParticipantItem';
import { useLeaveRoomConfirm } from 'features/Modals/ContinueCallConfirmModal/useLeaveRoomConfirm';
import { useParticipantMetadata } from 'useCase';
import { isFeatureOn } from 'useCase/featureFlags/featureFlagsApi';
import { useChat } from 'useCase/chat/useChat';
import { useModal } from 'useCase/modal/useModal';
import { roomInfoStore } from 'state/store';
import { selectIsAuthorized, selectOpenedModalName, selectRoomId, selectRoomMetadata } from 'state/selectors';
import { showRecordingSnackbar } from 'useCase/recording/useRecording';
import { useRoomInfo } from 'useCase/room/useRoomInfo';
import { useFullscreenStatus } from 'ui/hooks/useFullscreenStatus';
import { HOT_KEY, LoadingDots, useHotKey } from '@web-chat-core/ui-kit';
import { getOptimalVideoCodec } from 'utils/codecDetection';
import {
    clearModeratorBypassForRoom,
    consumePageReloading,
    isCallInitiator,
    markPageReloading,
    persistModeratorBypassForRoom,
} from 'features/AccessRequests/utils/canBypassWaitingRoom';
import { clearCallLsData } from 'features/PreJoin/utils/callLsData';
import { clearWaitingListRegistry } from 'features/AccessRequests/utils/waitingListRegistry';
import { shouldClearCachedAdmissionOnLeave } from 'features/PreJoin/utils/policy/admission';
import { clearLivekitTokens, clearWaitingQueue } from 'features/PreJoin/utils/waitingRoom/session';
import { useWaitingRoomNotifications } from 'features/AccessRequests/hooks/useWaitingRoomNotifications';
import { isLocalUserModerator } from 'useCase/moderation/status/moderators';
import {
    AUDIO_CONSTRAINTS,
    FEATURES,
    IN_ROOM,
    MESSAGE_FIELD_ID,
    STAGE_LAYOUT,
    StageLayout,
    TOKEN_STORAGE_KEY,
    VIDEO_PRESETS,
    VIDEO_QUALITY_LIMITS,
    WSSURL_STORAGE_KEY,
} from '../consts';
import { OptionsContainer, RoomContainer, Sidebar, SidebarContent } from './view';
import { useEmbeddedWindow } from './embedded/hooks/useEmbeddedWindow';

interface IProps {
    onRoomConnectionChanged?: () => void;
}

const HOLD_THRESHOLD_MS = 700;
const TOGGLE_THRESHOLD_MS = 200;

export const RoomPage = observer(({ onRoomConnectionChanged }: IProps) => {
    const { dynacast } = useSettingsContext();
    const sessionToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    const sessionWssUrl = sessionStorage.getItem(WSSURL_STORAGE_KEY);
    const userConfig = useUserSettingsContext();
    const { getDisplayData } = useParticipantItem();
    const [displayOptions, setDisplayOptions] = useState<DisplayOptions>({
        stageLayout: STAGE_LAYOUT.GRID,
    });
    const [inputValue, setInputValue] = useState<VideoQuality>(VideoQuality.LOW);
    const [outputValue, setOutputValue] = useState<VideoQuality>(VideoQuality.LOW);
    const [currentRoom, setCurrentRoom] = useState<Room>();
    const { closeModal } = useModal();
    const openedModal = selectOpenedModalName();
    const isAuthorized = selectIsAuthorized();
    const { prepareAndAddMessage, updateNewMessageCount } = useChat();
    const { setRoom, toggleStatisticVisibility, updateRoomMetadata } = useRoomInfo();
    const { broadcastParticipandAvatar } = useParticipantMetadata();
    const { isFullscreen, setFullscreen } = useFullscreenStatus();
    const { callCompleted } = useEmbeddedWindow();
    useWaitingRoomNotifications();
    const { cleanupOnLeave, getRoomProcessor } = useRoomVirtualBackground();
    useApplySpeakerVolume();
    useWebinarForceCamOff();
    useWebinarForceMicOff();
    useWebinarForceScreenShareOff();
    useWebinarModeNotifications();
    useMutedSpeechDetection();

    const isPushToTalkOn = isFeatureOn(FEATURES.VCS_MIC_PUSH_ENABLED);
    const isMicToggleOn = isFeatureOn(FEATURES.VCS_MIC_TOGGLE_ENABLED);

    const onDataReceived = useCallback(
        (payload: Uint8Array, participant?: RemoteParticipant, kind?: DataPacket_Kind, topic?: string) => {
            if (topic === 'message' && participant) {
                const name = getDisplayData(participant, participant instanceof LocalParticipant).name;
                const senderId = participant?.identity || '';
                const sid = participant?.sid || '';
                const text = new TextDecoder().decode(payload);
                prepareAndAddMessage({ text, own: false, name, senderId, sid });
                updateNewMessageCount();
            }
        },
        [getDisplayData],
    );

    useHotKey(HOT_KEY.TOGGLE_MEET_STATISTIC, toggleStatisticVisibility, { handlerName: 'toggle-stats' });

    const toggleCamera = useCallback(async () => {
        if (!currentRoom) return;
        if (isCameraHardLocked(currentRoom.localParticipant)) return;

        try {
            const currentState = currentRoom.localParticipant.isCameraEnabled;
            await currentRoom.localParticipant.setCameraEnabled(!currentState);

            userConfig.setVideoEnabled(!currentState);
        } catch (error) {
            console.error('Failed to toggle camera:', error);
        }
    }, [currentRoom, userConfig]);

    const setMicrophone = useCallback(
        async (value: boolean) => {
            if (!currentRoom) return;
            if (value && isMicHardLocked(currentRoom.localParticipant)) return;

            try {
                await currentRoom.localParticipant.setMicrophoneEnabled(value);

                userConfig.setAudioEnabled(value);
            } catch (error) {
                console.error('Failed to switch microphone:', error);
            }
        },
        [currentRoom, userConfig],
    );

    const toggleMicrophone = useCallback(async () => {
        if (!currentRoom) return;
        if (isMicHardLocked(currentRoom.localParticipant)) return;

        setMicrophone(!currentRoom.localParticipant.isMicrophoneEnabled);
    }, [currentRoom, setMicrophone]);

    const isMicrophoneEnabledOnHoldStart = useRef<null | boolean>(null);
    const pushToTalkStartedAtRef = useRef<number | null>(null);

    const handlePushToTalk = useCallback(
        (event: KeyboardEvent) => {
            if (!currentRoom) return;

            if (event.type === 'keydown') {
                isMicrophoneEnabledOnHoldStart.current = currentRoom.localParticipant.isMicrophoneEnabled;
                pushToTalkStartedAtRef.current = Date.now();

                if (isPushToTalkOn && !isMicrophoneEnabledOnHoldStart.current) {
                    setMicrophone(true);
                }
            } else if (event.type === 'keyup') {
                const startedAt = pushToTalkStartedAtRef.current;
                const isHold = startedAt ? Date.now() - startedAt >= HOLD_THRESHOLD_MS : false;
                const isToggle = startedAt ? Date.now() - startedAt <= TOGGLE_THRESHOLD_MS : false;
                const wasMicrophoneEnabledOnStart = Boolean(isMicrophoneEnabledOnHoldStart.current);

                if (!isMicrophoneEnabledOnHoldStart.current) {
                    if ((isPushToTalkOn && isHold) || !isMicToggleOn) {
                        setMicrophone(false);
                        return;
                    }
                }

                if (isMicToggleOn && isToggle) {
                    setMicrophone(!wasMicrophoneEnabledOnStart);
                    return;
                }

                isMicrophoneEnabledOnHoldStart.current = null;
                pushToTalkStartedAtRef.current = null;
            }
        },
        [currentRoom, isMicToggleOn, isPushToTalkOn, setMicrophone],
    );

    useHotKey(
        [HOT_KEY.SPACE],
        handlePushToTalk,
        {
            enabled: () =>
                Boolean(userConfig.permissions.audio) && Boolean(currentRoom) && (isPushToTalkOn || isMicToggleOn),
            preventDefault: true,
            keyUp: true,
            handlerName: 'push-to-talk',
            staticAvailabilityCheck: () => document.activeElement?.id !== MESSAGE_FIELD_ID,
        },
        [handlePushToTalk],
    );

    useHotKey(
        HOT_KEY.TOGGLE_MICRO,
        toggleMicrophone,
        {
            enabled: !!currentRoom,
            handlerName: 'toggle-micro',
        },
        [toggleMicrophone],
    );

    useHotKey(
        HOT_KEY.TOGGLE_CAMERA,
        toggleCamera,
        {
            enabled: !!currentRoom,
            handlerName: 'toggle-camera',
        },
        [toggleCamera],
    );

    useHotKey(HOT_KEY.ESC, closeModal, {
        enabled: !!currentRoom && !!openedModal,
        handlerName: 'close-modal',
    });

    const onChangeInputQuality = useCallback(
        (option: SelectOption) => {
            if (!currentRoom) {
                return;
            }
            const videoQuality = +option.value;
            // setting remote participants video quality
            currentRoom.remoteParticipants.forEach((p) => {
                p.videoTrackPublications.forEach((track, key) => {
                    console.log('%s: %s', key, videoQuality.toString());
                    console.log('SW', 'SETTING TO ', videoQuality);

                    track.setVideoQuality(videoQuality);
                });
            });
            setInputValue(videoQuality);
        },
        [currentRoom],
    );

    const onChangeOutputQuality = useCallback(
        async (option: SelectOption) => {
            let resolution;

            if (!currentRoom) {
                return;
            }

            if (option.value === VideoQuality.HIGH) {
                resolution = VIDEO_PRESETS.HIGH.resolution;
            }
            if (option.value === VideoQuality.MEDIUM) {
                resolution = VIDEO_PRESETS.MEDIUM.resolution;
            }
            if (option.value === VideoQuality.LOW) {
                resolution = VIDEO_PRESETS.LOW.resolution;
            }

            const videoQuality = +option.value;
            currentRoom.options.videoCaptureDefaults = {
                ...currentRoom.options.videoCaptureDefaults,
                resolution,
            };

            currentRoom.localParticipant.videoTrackPublications.forEach((track, key) => {
                console.log('lawParamsButton: %s: %s', key, videoQuality.toString());
                track.videoTrack?.setPublishingQuality(videoQuality);
            });
            setOutputValue(videoQuality);
        },
        [currentRoom],
    );

    const setStageLayout = useCallback((stageLayout: StageLayout) => {
        setDisplayOptions((prev) => {
            if (prev.stageLayout === stageLayout) {
                return prev;
            }

            return { ...prev, stageLayout };
        });
    }, []);

    useWebinarAutoLayout(setStageLayout);

    useEffect(() => {
        resetWebinarSpeakerLayoutOptOut();
    }, []);

    const onConnected = useCallback(
        (room: Room) => {
            setCurrentRoom(room);
            setLogLevel(debugLevel);
            void syncRoomWithUserSettings(room, userConfig);
            room.on(RoomEvent.DataReceived, onDataReceived);
            setRoom(room);
            sessionStorage.setItem(IN_ROOM, '1');

            const roomId = selectRoomId();
            if (roomId && (isLocalUserModerator() || isCallInitiator(roomId))) {
                persistModeratorBypassForRoom(roomId);
            }

            broadcastParticipandAvatar();
        },
        [broadcastParticipandAvatar, onDataReceived],
    );

    const onLeave = useCallback(() => {
        const roomId = selectRoomId();
        const clearCachedAdmissionForPolicy = Boolean(
            roomId && isAuthorized && shouldClearCachedAdmissionOnLeave(roomId),
        );

        void cleanupOnLeave();
        if (!isAuthorized) {
            sessionStorage.removeItem(TOKEN_STORAGE_KEY);
            sessionStorage.removeItem(WSSURL_STORAGE_KEY);
        } else if (clearCachedAdmissionForPolicy) {
            clearLivekitTokens();
        }
        sessionStorage.removeItem(IN_ROOM);
        clearWaitingQueue();
        clearWaitingListRegistry();

        const isPageReload = consumePageReloading();
        // Session-локи нужны при rejoin, чтобы не публиковать cam/mic из preview.
        // UI на prejoin дизейблим только после F5; после hangup кнопки остаются кликабельными.
        persistWebinarLocksForPrejoin(selectRoomMetadata()?.webinarMode, roomId, {
            lockPrejoinUi: isPageReload,
        });
        roomInfoStore.clearMetadata();
        roomInfoStore.setRoom(null);

        if (roomId && !isPageReload) {
            clearModeratorBypassForRoom(roomId);
        }
        // При явном выходе из комнаты (не F5) очищаем localStorage данные о звонке.
        if (!isPageReload) {
            clearCallLsData();
        }

        closeModal();
        onRoomConnectionChanged?.();
        callCompleted();
    }, [isAuthorized]);

    useEffect(() => {
        const onBeforeUnload = () => markPageReloading();
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, []);

    const handleActivateFullscreen = useCallback(() => {
        if (!isFullscreen) {
            setFullscreen();
        }
    }, [isFullscreen]);

    useLeaveRoomConfirm({ onLeave });

    useEffect(() => {
        if (!currentRoom) return;

        showRecordingSnackbar();
    }, [currentRoom]);

    const syncRoomOnReconnect = useEffectEvent((room: Room) => {
        void syncRoomWithUserSettings(room, userConfig);
        updateRoomMetadata(room.metadata);
    });

    useEffect(() => {
        if (!currentRoom) {
            return;
        }

        const handleReconnected = () => {
            syncRoomOnReconnect(currentRoom);
        };

        currentRoom.on(RoomEvent.Reconnected, handleReconnected);

        return () => {
            currentRoom.off(RoomEvent.Reconnected, handleReconnected);
        };
    }, [currentRoom, syncRoomOnReconnect]);

    const hasLivekitCredentials = Boolean(sessionWssUrl && sessionToken);

    if (!sessionWssUrl || !sessionToken) {
        return <div>url and token are required</div>;
    }

    // Изменен уровень логирования: 'warn' вместо 'debug' для снижения спама в консоли
    // 'debug' создавал огромное количество логов (trackSubscriptionStatusChanged, etc)
    const debugLevel = process.env.NODE_ENV === 'production' ? 'error' : 'warn';
    const processor = getRoomProcessor();

    if (!hasLivekitCredentials || !sessionWssUrl || !sessionToken) {
        return <LoadingDots />;
    }

    return (
        <DisplayContext.Provider value={{ ...displayOptions, setStageLayout }}>
            {/* Пока что скрываем Sidebar чтобы не давать вручную менять видео */}
            <Sidebar isOpen={false}>
                <SidebarContent>
                    <OptionsContainer>
                        <QualitySelector
                            value={inputValue}
                            label={'Input stream quality:'}
                            onChange={onChangeInputQuality}
                        />
                        <QualitySelector
                            value={outputValue}
                            label={'Output stream quality:'}
                            onChange={onChangeOutputQuality}
                        />
                    </OptionsContainer>
                </SidebarContent>
            </Sidebar>
            <FullscreenContext.Provider value={{ setFullscreenActive: handleActivateFullscreen }}>
                <RoomContainer $isFullscreen={isFullscreen}>
                    <LiveKitRoom
                        url={sessionWssUrl}
                        token={sessionToken}
                        onConnected={onConnected}
                        roomOptions={{
                            dynacast,
                            audioCaptureDefaults: {
                                ...AUDIO_CONSTRAINTS,
                                deviceId: userConfig.audioInputDeviceId,
                            },
                            videoCaptureDefaults: {
                                deviceId: userConfig.videoDeviceId,
                                resolution: VideoPresets.h720.resolution,
                                processor: processor || undefined,
                            },
                            publishDefaults: {
                                // ВАЖНО: simulcast должен быть включен для публикации всех слоев (LOW, MEDIUM, HIGH)
                                // Если simulcast выключен, будет публиковаться только один слой, и удаленные участники
                                // не смогут выбрать HIGH качество для спикера
                                simulcast: true, // Принудительно включаем simulcast для корректной работы оптимизации качества
                                videoSimulcastLayers: [VIDEO_PRESETS.LOW, VIDEO_PRESETS.MEDIUM, VIDEO_PRESETS.HIGH],
                                videoCodec: getOptimalVideoCodec(),
                                // Глобальное ограничение для всех слоев simulcast
                                // Гарантирует, что даже HIGH слой не превысит максимальные значения
                                videoEncoding: {
                                    maxBitrate: VIDEO_QUALITY_LIMITS.HIGH.maxBitrate,
                                    maxFramerate: VIDEO_QUALITY_LIMITS.HIGH.maxFramerate,
                                },
                                // Пресеты в videoSimulcastLayers определяют параметры каждого слоя:
                                // - HIGH: maxBitrate 1,200,000, maxFramerate 24 (≤4 участника)
                                // - MEDIUM: maxBitrate 350,000, maxFramerate 20 (5-9 участников)
                                // - LOW: maxBitrate 120,000, maxFramerate 15 (≥10 участников)
                                // setVideoQuality() выбирает нужный слой в зависимости от количества участников
                            },
                        }}
                        onLeave={onLeave}
                    />
                </RoomContainer>
            </FullscreenContext.Provider>
        </DisplayContext.Provider>
    );
});

async function syncRoomWithUserSettings(room: Room, userConfig: UserSettingsContent) {
    // make it easier to debug
    (window as any).currentRoom = room;

    const { audioInputDeviceId, videoDeviceId, permissions, setAudioEnabled, setVideoEnabled } = userConfig;
    const localParticipant = room.localParticipant;
    const persistedLocks = getPersistedWebinarLocks(selectRoomId());

    let audioEnabled = userConfig.audioEnabled;
    let videoEnabled = userConfig.videoEnabled;

    // Metadata может прийти позже connect — учитываем и hard-lock, и session-локи с prejoin.
    if (isMicHardLocked(localParticipant) || persistedLocks?.microDisabled) {
        audioEnabled = false;
        setAudioEnabled(false);
    }
    if (isCameraHardLocked(localParticipant) || persistedLocks?.cameraDisabled) {
        videoEnabled = false;
        setVideoEnabled(false);
    }

    // Проверки перед вызовами — избыточные setCameraEnabled/switchActiveDevice
    // пересоздают треки и вызывают мигания чёрным экраном
    if (permissions.audio) {
        if (localParticipant.isMicrophoneEnabled !== audioEnabled) {
            await localParticipant.setMicrophoneEnabled(audioEnabled);
        }

        // После await metadata/force-off могли успеть сменить политику — не оставляем трек ON.
        if (
            localParticipant.isMicrophoneEnabled &&
            (isMicHardLocked(localParticipant) || getPersistedWebinarLocks(selectRoomId())?.microDisabled)
        ) {
            setAudioEnabled(false);
            await localParticipant.setMicrophoneEnabled(false);
        }

        if (audioInputDeviceId && room.options.audioCaptureDefaults) {
            const currentAudioDeviceId = room.options.audioCaptureDefaults.deviceId;
            if (currentAudioDeviceId !== audioInputDeviceId) {
                try {
                    await room.switchActiveDevice('audioinput', audioInputDeviceId);
                } catch (e) {
                    console.error('Error switching audioinput device on sync', e);
                }
            }
        }
    }

    if (permissions.video) {
        if (localParticipant.isCameraEnabled !== videoEnabled) {
            await localParticipant.setCameraEnabled(videoEnabled);
        }

        if (
            localParticipant.isCameraEnabled &&
            (isCameraHardLocked(localParticipant) || getPersistedWebinarLocks(selectRoomId())?.cameraDisabled)
        ) {
            setVideoEnabled(false);
            await localParticipant.setCameraEnabled(false);
        }

        if (videoDeviceId && room.options.videoCaptureDefaults) {
            const currentVideoDeviceId = room.options.videoCaptureDefaults.deviceId;
            if (currentVideoDeviceId !== videoDeviceId) {
                try {
                    await room.switchActiveDevice('videoinput', videoDeviceId);
                } catch (e) {
                    console.error('Error switching videoinput device on sync', e);
                }
            }
        }
    }
}
